import type { Editor } from '@tiptap/react';

import { Toolbar as BaseToolbar, Tooltip as BaseTooltip } from '@base-ui/react';
import { useEditorState } from '@tiptap/react';
import { useTranslation } from 'react-i18next';

import { clsxs } from '~/styles/utils';

import type { Shortcode } from './RichTextEditor.types';

import { Button } from '../Button/Button';
import { Dialog } from '../Dialog/Dialog';
import { Menu } from '../Menu/Menu';
import { Separator } from '../Separator/Separator';
import { Toggle } from '../Toggle/Toggle';
import { Tooltip } from '../Tooltip/Tooltip';
import { InsertLinkDialog } from './InsertLinkDialog';
import { activeButtonStyle, buttonStyle, dividerStyle, toolbarGroupStyle, toolbarStyle } from './Toolbar.css';

type Props = {
  editor: Editor;
  shortcodes?: Shortcode[];
  withLinkButton: boolean;
};

export const Toolbar = ({ editor, shortcodes, withLinkButton }: Props) => {
  const { activeHref, canRedo, canUndo, hasSelection, isBold, isBulletList, isItalic, isLink, isOrderedList, isUnderlined } =
    useEditorState({
      editor,
      selector: (snapshot) => {
        // The editor can already be destroyed (e.g. its parent unmounted) by the time this
        // selector re-runs, in which case `commandManager` is null and `can()` throws.
        if (snapshot.editor.isDestroyed) {
          return {
            activeHref: undefined,
            canRedo: false,
            canUndo: false,
            hasSelection: false,
            isBold: false,
            isBulletList: false,
            isItalic: false,
            isLink: false,
            isOrderedList: false,
            isUnderlined: false,
          };
        }

        return {
          isBold: snapshot.editor.isActive('bold') ?? false,
          isItalic: snapshot.editor.isActive('italic') ?? false,
          isUnderlined: snapshot.editor.isActive('underline') ?? false,
          isLink: snapshot.editor.isActive('link') ?? false,
          isBulletList: snapshot.editor.isActive('bulletList') ?? false,
          isOrderedList: snapshot.editor.isActive('orderedList') ?? false,
          canUndo: snapshot.editor.can().chain().undo().run() ?? false,
          canRedo: snapshot.editor.can().chain().redo().run() ?? false,
          activeHref: (snapshot.editor.getAttributes('link') as { href?: string }).href,
          hasSelection: !snapshot.editor.state.selection.empty,
        };
      },
    });

  const { t } = useTranslation(['common']);

  const insertLink = ({ link, text }: { link: string; text: string }) => {
    const href = link.startsWith('http') ? link : `https://${link}`;

    if (isLink) {
      editor.chain().focus().extendMarkRange('link').setLink({ href, target: '_blank' }).run();
      return;
    }

    if (hasSelection) {
      editor.chain().focus().setLink({ href, target: '_blank' }).run();
      return;
    }

    editor
      .chain()
      .focus()
      .insertContent({ type: 'text', text, marks: [{ type: 'link', attrs: { href, target: '_blank' } }] })
      .unsetMark('link')
      .run();
  };

  const insertShortcode = (code: string) => editor.chain().focus().insertContent(code).run();

  return (
    <BaseToolbar.Root className={toolbarStyle}>
      <BaseTooltip.Provider delay={500}>
        <BaseToolbar.Group aria-label={t('common:richText.toolbar.groups.formatting')} className={toolbarGroupStyle}>
          <ToolbarToggle
            aria-label={t('common:richText.toolbar.bold')}
            icon="FormatBold"
            isActive={isBold}
            onClick={() => editor.chain().focus().toggleBold().run()}
          />

          <ToolbarToggle
            aria-label={t('common:richText.toolbar.italic')}
            icon="FormatItalics"
            isActive={isItalic}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          />

          <ToolbarToggle
            aria-label={t('common:richText.toolbar.underline')}
            icon="FormatUnderlined"
            isActive={isUnderlined}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          />

          {withLinkButton && (
            <Dialog.Root>
              <Dialog.Trigger
                render={
                  <ToolbarButton
                    aria-label={t(isLink ? 'common:richText.toolbar.editLink' : 'common:richText.toolbar.link')}
                    icon="Link"
                    isActive={isLink}
                  />
                }
              />

              <InsertLinkDialog link={activeHref} onInsert={insertLink} withTextField={!hasSelection && !isLink} />
            </Dialog.Root>
          )}
        </BaseToolbar.Group>

        <Separator className={dividerStyle} orientation="vertical" />

        <BaseToolbar.Group aria-label={t('common:richText.toolbar.groups.lists')} className={toolbarGroupStyle}>
          <ToolbarToggle
            aria-label={t('common:richText.toolbar.bulletList')}
            icon="FormatListBulleted"
            isActive={isBulletList}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          />

          <ToolbarToggle
            aria-label={t('common:richText.toolbar.orderedList')}
            icon="FormatListNumbered"
            isActive={isOrderedList}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          />
        </BaseToolbar.Group>

        {shortcodes && (
          <>
            <Separator className={dividerStyle} orientation="vertical" />

            <BaseToolbar.Group aria-label={t('common:richText.toolbar.groups.shortcodes')} className={toolbarGroupStyle}>
              <Menu.Root>
                <Menu.Trigger render={<ToolbarButton aria-label={t('common:richText.toolbar.shortcodes')} icon="ChevronDown" />} />

                <Menu.List>
                  {shortcodes.map(({ code, label }) => (
                    <Menu.Item key={code} onClick={() => insertShortcode(code)}>
                      {label}
                    </Menu.Item>
                  ))}
                </Menu.List>
              </Menu.Root>
            </BaseToolbar.Group>
          </>
        )}

        <Separator className={dividerStyle} orientation="vertical" />

        <BaseToolbar.Group aria-label={t('common:richText.toolbar.groups.history')} className={toolbarGroupStyle}>
          <ToolbarButton
            aria-label={t('common:richText.toolbar.undo')}
            disabled={!canUndo}
            icon="Undo"
            onClick={() => editor.commands.undo()}
          />

          <ToolbarButton
            aria-label={t('common:richText.toolbar.redo')}
            disabled={!canRedo}
            icon="Redo"
            onClick={() => editor.commands.redo()}
          />
        </BaseToolbar.Group>
      </BaseTooltip.Provider>
    </BaseToolbar.Root>
  );
};

type ToolbarToggleProps = Parameters<typeof Toggle>[0] & {
  isActive: boolean;
};

const ToolbarToggle = ({ disabled, isActive, ...delegated }: ToolbarToggleProps) => {
  return (
    <Tooltip content={delegated['aria-label']} triggerId={delegated.id}>
      <BaseToolbar.Button
        disabled={disabled}
        focusableWhenDisabled={false}
        render={
          <Toggle {...delegated} className={clsxs(buttonStyle, isActive && activeButtonStyle, delegated.className)} pressed={isActive} />
        }
      />
    </Tooltip>
  );
};

type ToolbarButtonProps = Parameters<typeof Button>[0] & {
  isActive?: boolean;
};

const ToolbarButton = ({ disabled, isActive = false, ...delegated }: ToolbarButtonProps) => {
  return (
    <Tooltip content={delegated['aria-label']} triggerId={delegated.id}>
      <BaseToolbar.Button
        disabled={disabled}
        focusableWhenDisabled={false}
        render={<Button {...delegated} className={clsxs(buttonStyle, isActive && activeButtonStyle, delegated.className)} type="button" />}
      />
    </Tooltip>
  );
};
